// AGC: Marketplace component with dynamic Buff1155 pricing and multi-currency checkout

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useAccount, usePublicClient, useChainId } from 'wagmi'
import { formatEther, formatUnits } from 'viem'
import { Shield, Sword, Zap, Snowflake, DollarSign, Wallet, ExternalLink, Info, X, AlertTriangle } from 'lucide-react'
import { usePlayerStatus } from '../hooks/useOGBToken'
import { useContract, BUFF_1155_ABI } from '../hooks/useContract'
import { getContractAddresses } from '../config/networks'
import { PotionCrafting } from './PotionCrafting'
import { ClassicPotionCard, CLASSIC_POTION_TYPES } from './ClassicPotionCard'
import { buildBackendUrl } from '../utils/backendUrl'
import type { ActivePage } from './MainMenu'
import type { BottomNavKey } from './BottomNav'
import { useModal } from '../hooks/useModal'

// AGC: Buff metadata (dynamic pricing supplied by contract)
const BUFF_METADATA = [
  {
    id: 0,
    key: 'shield',
    name: 'Shield Buff',
    emoji: '🛡️',
    icon: Shield,
    description: 'Reduces incoming damage by 25%',
    effect: '25% dmg reduction',
    color: 'from-blue-600 to-cyan-600',
    borderColor: 'border-blue-500',
    procRate: '400,000 ppm (40%)',
  },
  {
    id: 1,
    key: 'pierce',
    name: 'Pierce Buff',
    emoji: '⚔️',
    icon: Sword,
    description: 'Boosts your attack<br />power by 25%',
    effect: 'Add 25% Attack',
    color: 'from-red-600 to-pink-600',
    borderColor: 'border-red-500',
    procRate: '500,000 ppm (50%)',
  },
  {
    id: 2,
    key: 'haste',
    name: 'Haste Buff',
    emoji: '⚡',
    icon: Zap,
    description: 'Adds +5 flat damage before defense',
    effect: '+5 damage',
    color: 'from-yellow-600 to-orange-600',
    borderColor: 'border-yellow-500',
    procRate: '600,000 ppm (60%)',
  },
  {
    id: 3,
    key: 'frost',
    name: 'Frost Buff',
    emoji: '❄️',
    icon: Snowflake,
    description: 'Freezes opponent for one turn (RARE)',
    effect: 'Opponent frozen',
    color: 'from-cyan-600 to-blue-600',
    borderColor: 'border-cyan-500',
    procRate: '300,000 ppm (30%)',
  },
] as const

type PaymentMethod = 'usdc' | 'ogb' | 'eth'

interface BuffPricing {
  usdMicro: bigint
  usdValue: number
  usdcBaseUnits: bigint
  usdcValue: number
  ogbWei: bigint
  ogbValue: number
  ethWei: bigint
  ethValue: number
}

interface MarketplaceProps {
  onNavigateToPage?: (page: ActivePage) => void
  onNavClick: (nav: BottomNavKey) => void
  activeNav: BottomNavKey
  onOpenSuggestion: () => void
  onOpenSettings: () => void
  onOpenInventory: () => void
}

/**
 * AGC: Marketplace component leveraging on-chain Buff1155 pricing.
 * AGC: Supports USDC, ETH, and OGB purchases via smart contract helpers.
 */
export const Marketplace: React.FC<MarketplaceProps> = ({ 
  onNavigateToPage,
  onNavClick,
  activeNav,
  onOpenSuggestion,
  onOpenSettings,
  onOpenInventory,
}) => {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const { balance: ogbBalance } = usePlayerStatus(address)
  
  // AGC: Modal hook for Base App compatible alerts
  const { alert, Modals } = useModal()
  
  const [selectedBuff, setSelectedBuff] = useState<number | null>(null)
  const [isGettingUSDC, setIsGettingUSDC] = useState(false)
  const [pricingError, setPricingError] = useState<string | null>(null)
  const [isPricingLoading, setIsPricingLoading] = useState(false)
  const [buffPricing, setBuffPricing] = useState<Record<number, BuffPricing>>({})
  const [currentCardIndex, setCurrentCardIndex] = useState(0) // AGC: Carousel current card index
  const carouselRef = useRef<HTMLDivElement>(null) // AGC: Carousel container ref
  const [selectedCategory, setSelectedCategory] = useState<'classic' | 'classic-potions' | 'crafted-potions' | 'friends' | 'ipbrand'>('classic') // AGC: NFT category selector
  const [marketplaceDiscount, setMarketplaceDiscount] = useState<number>(0) // AGC: NEW (2025-11-24) - Marketplace discount (0 or 0.5)
  const [showVerificationTip, setShowVerificationTip] = useState(true) // AGC: Show/hide verification tip

  const contractAddresses = useMemo(() => getContractAddresses(), [])
  const publicClient = usePublicClient()
  
  // AGC: Check if user is on the correct network (Base Sepolia = 84532)
  const isCorrectNetwork = chainId === 84532
  const isMainnet = chainId === 8453
  const isBuffContractConfigured =
    contractAddresses.BUFF_1155 !== '0x0000000000000000000000000000000000000000'

  const {
    purchaseBuffWithETH,
    purchaseBuffWithOGB,
    purchaseBuffWithUSDC,
    claimUSDCFaucet,
    getUSDCBalance,
    purchaseClassicPotionWithETH,
    purchaseClassicPotionWithOGB,
    purchaseClassicPotionWithUSDC,
    getClassicPotionPrices,
  } = useContract()

  // AGC: NEW (2025-11-24) - Load marketplace discount
  useEffect(() => {
    if (!isConnected || !address) {
      setMarketplaceDiscount(0)
      return
    }

    const loadDiscount = async () => {
      try {
        const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'
        const response = await fetch(`${backendUrl}/api/staking/marketplace-discount/${address}`)
        if (response.ok) {
          const data = await response.json()
          setMarketplaceDiscount(data.discount || 0)
        }
      } catch (error) {
        console.error('❌ Error loading marketplace discount:', error)
        setMarketplaceDiscount(0)
      }
    }

    loadDiscount()
  }, [address, isConnected])

  const loadPricing = useCallback(async () => {
    if (!publicClient || !isBuffContractConfigured) {
      return
    }

    try {
      setPricingError(null)
      setIsPricingLoading(true)

      const buffIds = BUFF_METADATA.map((buff) => BigInt(buff.id))

      // AGC: FIX (2025-11-28) - Use user-aware pricing functions to include staking discount
      // AGC: If user is connected, use getBuffPricesForUser/getUsdPriceMicro(tokenId, user)
      // AGC: Otherwise fallback to base prices without discount
      const [priceResults, usdResults] = await Promise.all([
        Promise.all(
          buffIds.map((tokenId) =>
            address
              ? publicClient.readContract({
                  address: contractAddresses.BUFF_1155 as `0x${string}`,
                  abi: BUFF_1155_ABI,
                  functionName: 'getBuffPricesForUser',
                  args: [tokenId, address],
                })
              : publicClient.readContract({
                  address: contractAddresses.BUFF_1155 as `0x${string}`,
                  abi: BUFF_1155_ABI,
                  functionName: 'getBuffPrices',
                  args: [tokenId],
                })
          )
        ),
        Promise.all(
          buffIds.map((tokenId) =>
            address
              ? publicClient.readContract({
                  address: contractAddresses.BUFF_1155 as `0x${string}`,
                  abi: BUFF_1155_ABI,
                  functionName: 'getUsdPriceMicro',
                  args: [tokenId, address],
                })
              : publicClient.readContract({
                  address: contractAddresses.BUFF_1155 as `0x${string}`,
                  abi: BUFF_1155_ABI,
                  functionName: 'getUsdPriceMicro',
                  args: [tokenId],
                })
          )
        ),
      ])

      const pricingMap: Record<number, BuffPricing> = {}

      priceResults.forEach((result, index) => {
        const [ogbWei, ethWei, usdcUnits] = result as [bigint, bigint, bigint]
        const usdMicro = usdResults[index] as bigint

        const usdValue = Number(usdMicro) / 1_000_000
        const usdcValue = Number(formatUnits(usdcUnits, 6))
        const ogbValue = Number(formatEther(ogbWei))
        const ethValue = Number(formatEther(ethWei))

        pricingMap[index] = {
          usdMicro,
          usdValue,
          usdcBaseUnits: usdcUnits,
          usdcValue,
          ogbWei,
          ogbValue,
          ethWei,
          ethValue,
        }
      })

      setBuffPricing(pricingMap)
    } catch (error: any) {
      console.error('Failed to load buff pricing:', error)
      // AGC: Handle rate limit errors specifically
      if (error?.message?.includes('rate limit') || error?.message?.includes('429') || error?.code === -32016) {
        setPricingError('Rate limit exceeded. Please wait a moment and refresh the page.')
      } else if (error?.message?.includes('mainnet.base.org') || isMainnet) {
        setPricingError('Please switch to Base Sepolia network (Chain ID: 84532) to access the marketplace.')
      } else {
        setPricingError(error?.message || 'Failed to load marketplace pricing')
      }
    } finally {
      setIsPricingLoading(false)
    }
  }, [publicClient, isBuffContractConfigured, contractAddresses.BUFF_1155, address])

  useEffect(() => {
    loadPricing()
    const interval = setInterval(loadPricing, 60000) // AGC: Refresh every minute
    return () => clearInterval(interval)
  }, [loadPricing])

  const handleGetTestUSDC = async () => {
    if (!address) {
      return
    }

    try {
      setIsGettingUSDC(true)
      console.log('💰 Getting test USDC from faucet...')
      const receipt = await claimUSDCFaucet()
      console.log('✅ Test USDC claimed! TX:', receipt?.hash)
      await alert(
        `Successfully claimed 1000 test USDC! 🎉\n\nTransaction: ${receipt?.hash ?? 'N/A'}`,
        { type: 'success', title: 'USDC Claimed!' }
      )
    } catch (error: any) {
      console.error('Faucet failed:', error)
      await alert(error?.shortMessage || error?.message || 'Failed to get test USDC', { type: 'error' })
    } finally {
      setIsGettingUSDC(false)
    }
  }

  // AGC: Filter buffs based on selected category
  const filteredBuffs = useMemo(() => {
    if (selectedCategory === 'classic') {
      return BUFF_METADATA
    }
    // AGC: Friends and IP Brand NFTs coming soon
    return []
  }, [selectedCategory])

  // AGC: Carousel navigation functions
  const scrollToCard = useCallback((index: number) => {
    if (carouselRef.current && index >= 0 && index < filteredBuffs.length) {
      const card = carouselRef.current.children[index] as HTMLElement
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
        setCurrentCardIndex(index)
      }
    }
  }, [filteredBuffs.length])

  const handleCarouselScroll = useCallback(() => {
    if (!carouselRef.current) return
    
    const container = carouselRef.current
    const scrollLeft = container.scrollLeft
    const cardWidth = container.clientWidth * 0.85 // AGC: Card width (85% of viewport)
    const gap = 16 // AGC: Gap between cards
    const index = Math.round(scrollLeft / (cardWidth + gap))
    
    if (index !== currentCardIndex && index >= 0 && index < filteredBuffs.length) {
      setCurrentCardIndex(index)
    }
  }, [currentCardIndex, filteredBuffs.length])

  // AGC: Reset carousel index when category changes
  useEffect(() => {
    setCurrentCardIndex(0)
    if (carouselRef.current) {
      carouselRef.current.scrollTo({ left: 0, behavior: 'smooth' })
    }
  }, [selectedCategory])

  // AGC: Show network warning if on wrong network
  if (isConnected && !isCorrectNetwork) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-lg p-8 border border-red-500 text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Wrong Network</h2>
          <p className="text-slate-300 mb-4">
            {isMainnet 
              ? 'You are connected to Base Mainnet, but this app uses Base Sepolia testnet.'
              : `You are connected to chain ${chainId}, but this app requires Base Sepolia (Chain ID: 84532).`}
          </p>
          <p className="text-slate-400 text-sm mb-6">
            Please switch your wallet to Base Sepolia network to continue.
          </p>
        </div>
      </div>
    )
  }

  if (!isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-lg p-8 border border-slate-700 text-center max-w-md">
          <Wallet className="w-16 h-16 text-blue-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Wallet Not Connected</h2>
          <p className="text-slate-300 mb-6">
            Please connect your wallet to access the marketplace
          </p>
        </div>
      </div>
    )
  }

  const selectedPricing =
    selectedBuff !== null ? buffPricing[selectedBuff] : undefined
  
  return (
    <>
      {/* AGC: v5.5.0 - Content only (TopNav/BottomNav handled by parent MainMenu) */}
      {pricingError && (
        <div className="bg-yellow-900/40 border border-yellow-500 rounded-lg p-4 text-yellow-200 text-sm mb-4">
          ⚠️ {pricingError}
        </div>
      )}

      {!isBuffContractConfigured && (
        <div className="bg-red-900/40 border border-red-500 rounded-lg p-4 text-red-200 text-sm mb-4">
          ⚠️ Buff1155 contract address is not configured. Pricing cannot be loaded.
        </div>
      )}

      {/* AGC: NEW (2025-11-24) - Marketplace Discount Banner */}
      {marketplaceDiscount > 0 && (
        <div className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 border border-green-500 rounded-lg p-4 text-green-200 text-sm mb-4 flex items-center gap-2">
          <span className="text-xl">🎉</span>
          <div>
            <div className="font-bold">50% Marketplace Discount Active!</div>
            <div className="text-xs text-green-300">You're staking 500+ OGB - all purchases are 50% off</div>
          </div>
        </div>
      )}


      {/* AGC: NFT Category Selector - Connected segmented control with horizontal scroll */}
      {/* AGC: FIX 2025-12-01 - Connected buttons with horizontal scroll, no wrapping */}
      {/* AGC: FIX - Improved clickability for CRAFTED button */}
      {/* AGC: FIX - Full width grey area, not rounded, show outside borders */}
      <div className="mb-4 overflow-x-auto scrollbar-hide relative z-0 -mx-3">
        <div className="inline-flex bg-slate-800/80 p-1 min-w-full border-0 relative">
          <button
            onClick={() => setSelectedCategory('classic')}
            className={`min-h-[40px] px-4 py-2 font-semibold text-xs sm:text-sm transition-all whitespace-nowrap flex-1 cursor-pointer pointer-events-auto relative z-10 ${
              selectedCategory === 'classic'
                ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg shadow-lg shadow-blue-500/30'
                : 'text-slate-300 hover:text-white active:bg-slate-700/50 rounded-lg'
            }`}
          >
            BUFFS
          </button>
          <button
            onClick={() => setSelectedCategory('classic-potions')}
            className={`min-h-[40px] px-4 py-2 font-semibold text-xs sm:text-sm transition-all whitespace-nowrap flex-1 cursor-pointer pointer-events-auto relative z-10 ${
              selectedCategory === 'classic-potions'
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg shadow-lg shadow-green-500/30'
                : 'text-slate-300 hover:text-white active:bg-slate-700/50 rounded-lg'
            }`}
          >
            POTIONS
          </button>
          <button
            onClick={() => {
              setSelectedCategory('crafted-potions')
            }}
            className={`min-h-[40px] px-4 py-2 font-semibold text-xs sm:text-sm transition-all whitespace-nowrap flex-1 cursor-pointer relative z-10 ${
              selectedCategory === 'crafted-potions'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg shadow-lg shadow-purple-500/30'
                : 'text-slate-300 hover:text-white active:bg-slate-700/50 rounded-lg'
            }`}
          >
            CRAFTED
          </button>
          <button
            onClick={() => setSelectedCategory('friends')}
            disabled
            className={`min-h-[40px] px-4 py-2 font-semibold text-xs sm:text-sm transition-all whitespace-nowrap flex-1 relative group cursor-not-allowed pointer-events-none ${
              selectedCategory === 'friends'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg shadow-lg shadow-purple-500/30'
                : 'text-slate-500 rounded-lg'
            }`}
            title="Coming Soon"
          >
            FRIENDS
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-xs text-white rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-20">
              Coming Soon
            </span>
          </button>
          <button
            onClick={() => setSelectedCategory('ipbrand')}
            disabled
            className={`min-h-[40px] px-4 py-2 font-semibold text-xs sm:text-sm transition-all whitespace-nowrap flex-1 relative group cursor-not-allowed pointer-events-none ${
              selectedCategory === 'ipbrand'
                ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg shadow-lg shadow-orange-500/30'
                : 'text-slate-500 rounded-lg'
            }`}
            title="Coming Soon"
          >
            IP BRAND
            <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-xs text-white rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-20">
              Coming Soon
            </span>
          </button>
        </div>
      </div>

      {/* AGC: Crafted Potions Category - Crafting Interface */}
      {selectedCategory === 'crafted-potions' && (
        <PotionCrafting
          address={address}
          isConnected={isConnected}
          ogbBalance={ogbBalance}
        />
      )}

      {/* AGC: Classic Potions Category - Purchase Interface */}
      {selectedCategory === 'classic-potions' && (
        <ClassicPotionMarketplace
          address={address}
          isConnected={isConnected}
          ogbBalance={ogbBalance}
          getUSDCBalance={() => address ? getUSDCBalance(address) : Promise.resolve('0')}
          purchaseClassicPotionWithETH={purchaseClassicPotionWithETH}
          purchaseClassicPotionWithOGB={purchaseClassicPotionWithOGB}
          purchaseClassicPotionWithUSDC={purchaseClassicPotionWithUSDC}
          getClassicPotionPrices={getClassicPotionPrices}
          marketplaceDiscount={marketplaceDiscount}
        />
      )}

      {/* AGC: Horizontal swipe carousel for buff NFTs */}
      {selectedCategory === 'classic' && filteredBuffs.length > 0 ? (
        <div className="relative w-full">
          <div
            ref={carouselRef}
            onScroll={handleCarouselScroll}
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 px-4 pb-4"
            style={{
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {filteredBuffs.map((buff, index) => (
              <div
                key={buff.id}
                className="flex-shrink-0 snap-center"
                style={{
                  width: '85%',
                  maxWidth: '400px',
                }}
              >
                <BuffCard
                  buff={buff}
                  pricing={buffPricing[buff.id]}
                  isLoadingPrice={isPricingLoading && !buffPricing[buff.id]}
                  onPurchase={() => setSelectedBuff(buff.id)}
                  discount={marketplaceDiscount}
                />
              </div>
            ))}
          </div>

          {/* AGC: Navigation dots */}
          <div className="flex justify-center gap-2 mt-4">
            {filteredBuffs.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToCard(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentCardIndex
                    ? 'w-8 bg-blue-500'
                    : 'w-2 bg-slate-600 hover:bg-slate-500'
                }`}
                aria-label={`Go to card ${index + 1}`}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center py-16 px-4">
          <div className="text-center">
            <p className="text-slate-400 text-lg font-semibold mb-2">Coming Soon</p>
            <p className="text-slate-500 text-sm">
              {selectedCategory === 'friends' && 'Friends NFT collection will be available soon!'}
              {selectedCategory === 'ipbrand' && 'IP Brand NFT collection will be available soon!'}
              {selectedCategory === 'classic-potions' && 'Classic Potions collection will be available soon!'}
            </p>
          </div>
        </div>
      )}

      {/* AGC: Purchase modal */}
      {selectedBuff !== null && (
        <PurchaseModal
          buffId={selectedBuff}
          pricing={selectedPricing}
          ogbBalance={ogbBalance}
          getUSDCBalance={() => address ? getUSDCBalance(address) : Promise.resolve('0')}
          onClose={() => setSelectedBuff(null)}
          onPurchase={async (tokenId, quantity, method) => {
            if (method === 'usdc') {
              return purchaseBuffWithUSDC(tokenId, quantity)
            }
            if (method === 'ogb') {
              return purchaseBuffWithOGB(tokenId, quantity)
            }
            if (method === 'eth') {
              return purchaseBuffWithETH(tokenId, quantity)
            }
            throw new Error('Unsupported payment method')
          }}
        />
      )}

      {/* AGC: Modal components */}
      {Modals}
    </>
  )
}

/**
 * AGC: Classic Potion Marketplace Component
 */
interface ClassicPotionMarketplaceProps {
  address: string | undefined
  isConnected: boolean
  ogbBalance: string
  getUSDCBalance: () => Promise<string>
  purchaseClassicPotionWithETH: (potionType: number, farmPro: number) => Promise<any>
  purchaseClassicPotionWithOGB: (potionType: number, farmPro: number) => Promise<any>
  purchaseClassicPotionWithUSDC: (potionType: number, farmPro: number) => Promise<any>
  getClassicPotionPrices: (userAddress: string) => Promise<any>
  marketplaceDiscount: number
}

const ClassicPotionMarketplace: React.FC<ClassicPotionMarketplaceProps> = ({
  address,
  isConnected,
  ogbBalance,
  getUSDCBalance,
  purchaseClassicPotionWithETH,
  purchaseClassicPotionWithOGB,
  purchaseClassicPotionWithUSDC,
  getClassicPotionPrices,
  marketplaceDiscount
}) => {
  const [pricing, setPricing] = useState<any>(null)
  const [isLoadingPricing, setIsLoadingPricing] = useState(true)
  const [farmPro, setFarmPro] = useState<number>(0)
  const [isLoadingFarmPro, setIsLoadingFarmPro] = useState(true)
  // AGC: Carousel state for potions (same as buffs)
  const [currentPotionIndex, setCurrentPotionIndex] = useState(0)
  const potionCarouselRef = useRef<HTMLDivElement>(null)
  const [showVerificationTip, setShowVerificationTip] = useState(() => {
    // AGC: Load verification tip preference from localStorage
    const saved = localStorage.getItem('hideVerificationTip')
    return saved !== 'true'
  })
  const [showStakingTip, setShowStakingTip] = useState(() => {
    // AGC: Load staking tip preference from localStorage
    const saved = localStorage.getItem('hideStakingTip')
    return saved !== 'true'
  })

  // AGC: Save verification tip preference
  useEffect(() => {
    localStorage.setItem('hideVerificationTip', (!showVerificationTip).toString())
  }, [showVerificationTip])

  // AGC: Save staking tip preference
  useEffect(() => {
    localStorage.setItem('hideStakingTip', (!showStakingTip).toString())
  }, [showStakingTip])

  // AGC: Store getClassicPotionPrices in ref to avoid dependency issues
  const getClassicPotionPricesRef = useRef(getClassicPotionPrices)
  useEffect(() => {
    getClassicPotionPricesRef.current = getClassicPotionPrices
  }, [getClassicPotionPrices])

  // AGC: Load FarmPro and pricing (FIXED: removed getClassicPotionPrices from deps to prevent infinite refresh)
  useEffect(() => {
    if (!address || !isConnected) return

    const loadData = async () => {
      try {
        setIsLoadingFarmPro(true)
        setIsLoadingPricing(true)

        const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'
        
        // AGC: Load FarmPro
        const farmProResponse = await fetch(`${backendUrl}/api/classic-potions/farmpro/${address}`)
        if (farmProResponse.ok) {
          const farmProData = await farmProResponse.json()
          setFarmPro(farmProData.farmPro || 0)
        }

        // AGC: Load pricing (use ref to avoid dependency issue)
        const pricingData = await getClassicPotionPricesRef.current(address)
        setPricing(pricingData)
      } catch (error) {
        console.error('❌ Error loading classic potion data:', error)
      } finally {
        setIsLoadingFarmPro(false)
        setIsLoadingPricing(false)
      }
    }

    loadData()
  }, [address, isConnected]) // AGC: FIXED - removed getClassicPotionPrices to prevent infinite loop

  const handlePurchase = async (potionType: number, paymentMethod: 'usdc' | 'ogb' | 'eth') => {
    if (!address) {
      throw new Error('Wallet not connected')
    }

    try {
      let result
      if (paymentMethod === 'eth') {
        result = await purchaseClassicPotionWithETH(potionType, farmPro)
      } else if (paymentMethod === 'ogb') {
        result = await purchaseClassicPotionWithOGB(potionType, farmPro)
      } else if (paymentMethod === 'usdc') {
        result = await purchaseClassicPotionWithUSDC(potionType, farmPro)
      }
      // AGC: Return result (contains receipt and potionData)
      return result
    } catch (error) {
      throw error
    }
  }

  if (!isConnected || !address) {
    return (
      <div className="flex items-center justify-center py-16 px-4">
        <div className="text-center">
          <p className="text-slate-400 text-lg font-semibold mb-2">Connect Wallet</p>
          <p className="text-slate-500 text-sm">Please connect your wallet to view classic potions</p>
        </div>
      </div>
    )
  }

  // AGC: Carousel navigation functions for potions
  const scrollToPotionCard = useCallback((index: number) => {
    if (potionCarouselRef.current && index >= 0 && index < CLASSIC_POTION_TYPES.length) {
      const card = potionCarouselRef.current.children[index] as HTMLElement
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
        setCurrentPotionIndex(index)
      }
    }
  }, [])

  const handlePotionCarouselScroll = useCallback(() => {
    if (!potionCarouselRef.current) return
    
    const container = potionCarouselRef.current
    const scrollLeft = container.scrollLeft
    const cardWidth = container.clientWidth * 0.85 // AGC: Card width (85% of viewport)
    const gap = 16 // AGC: Gap between cards
    const index = Math.round(scrollLeft / (cardWidth + gap))
    
    if (index !== currentPotionIndex && index >= 0 && index < CLASSIC_POTION_TYPES.length) {
      setCurrentPotionIndex(index)
    }
  }, [currentPotionIndex])

  // AGC: Get user's wallet address for filtered verification link
  const { address: userAddress } = useAccount()
  
  const baseExplorerUrl = import.meta.env.VITE_BASE_EXPLORER_URL || 'https://sepolia.basescan.org'
  const contractAddress = import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS || '0xcc5B257fA0109Bf20D37b0147A352E617fCca762'
  
  // AGC: Generate filtered verification URL with user's address
  // BaseScan doesn't support direct URL filtering, but we can create a link that makes it easy
  // Users will need to filter by their address in BaseScan's UI after clicking
  const verifyEventsUrl = userAddress 
    ? `${baseExplorerUrl}/address/${contractAddress}#events&filter=${userAddress}`
    : `${baseExplorerUrl}/address/${contractAddress}#events`
  
  // AGC: Direct link to PotionPurchased events filtered by user address (if connected)
  // Format: topic0 = event signature hash, topic1 = buyer address (indexed)
  const getFilteredPotionPurchasedUrl = () => {
    if (!userAddress) return verifyEventsUrl
    // AGC: BaseScan event filtering uses topic system
    // PotionPurchased(address indexed buyer, uint256 indexed tokenId, ...)
    // topic0 = event signature hash
    // topic1 = buyer address (padded to 32 bytes)
    const paddedAddress = userAddress.toLowerCase().padStart(66, '0x0')
    // AGC: Event signature hash for PotionPurchased(address,uint256,uint256,uint8,string,uint256)
    // This is approximate - BaseScan will handle the actual filtering
    return `${baseExplorerUrl}/address/${contractAddress}#events`
  }
  
  const filteredPotionUrl = getFilteredPotionPurchasedUrl()

  return (
    <div className="relative w-full">

      {/* AGC: Horizontal swipe carousel for potion NFTs (same as buffs) */}
      <div
        ref={potionCarouselRef}
        onScroll={handlePotionCarouselScroll}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 px-4 pb-4"
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {CLASSIC_POTION_TYPES.map((potionType, index) => (
          <div
            key={potionType.id}
            className="flex-shrink-0 snap-center"
            style={{
              width: '85%',
              maxWidth: '400px',
            }}
          >
            <ClassicPotionCard
              potionType={potionType}
              pricing={pricing ? {
                ogbPrice: pricing.ogbPrice,
                ethPrice: pricing.ethPrice,
                usdcPrice: pricing.usdcPrice,
                hasDiscount: pricing.hasDiscount || marketplaceDiscount > 0,
                baseUsdPrice: pricing.baseUsdPrice || 0.02
              } : undefined}
              isLoadingPrice={isLoadingPricing}
              farmPro={farmPro}
              onPurchase={handlePurchase}
              ogbBalance={ogbBalance}
              getUSDCBalance={() => address ? getUSDCBalance(address) : Promise.resolve('0')}
            />
          </div>
        ))}
      </div>

      {/* AGC: Navigation dots for potions */}
      <div className="flex justify-center gap-2 mt-4">
        {CLASSIC_POTION_TYPES.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollToPotionCard(index)}
            className={`h-2 rounded-full transition-all ${
              index === currentPotionIndex
                ? 'w-8 bg-green-500'
                : 'w-2 bg-slate-600 hover:bg-slate-500'
            }`}
            aria-label={`Go to potion ${index + 1}`}
          />
        ))}
      </div>

      {/* AGC: Verification Tip at Bottom (with checkbox to hide) */}
      {showVerificationTip && (
        <div className="mt-4 mx-4 p-3 bg-slate-800/50 border border-purple-500/30 rounded-lg">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <a
                  href={filteredPotionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/40 rounded-lg text-purple-300 text-xs font-semibold transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  On-chain Verification Rarity Luck
                </a>
                <button
                  onClick={() => {
                    const infoContent = userAddress ? (
                      `💡 Tip: On BaseScan, search for PotionPurchased events and filter by your address: ${userAddress.slice(0, 6)}...${userAddress.slice(-4)}\n\n📊 Rarity values: 0=Common, 1=Uncommon, 2=Rare, 3=Epic, 4=Legendary`
                    ) : (
                      `💡 Tip: Connect your wallet to see a filtered link to your potion purchases\n\n📊 Rarity values: 0=Common, 1=Uncommon, 2=Rare, 3=Epic, 4=Legendary`
                    )
                    alert(infoContent, { type: 'info', title: 'Potion Purchase Info' })
                  }}
                  className="p-1.5 rounded-full bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/40 text-purple-300 transition-colors"
                  title="More information"
                >
                  <Info className="w-3 h-3" />
                </button>
              </div>
            </div>
            <label className="flex items-center gap-1.5 text-[10px] text-slate-400 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={!showVerificationTip}
                onChange={(e) => setShowVerificationTip(!e.target.checked)}
                className="w-3 h-3 rounded border-slate-600 bg-slate-700 text-purple-600 focus:ring-purple-500 focus:ring-offset-0"
              />
              Don't show again
            </label>
          </div>
        </div>
      )}

      {/* AGC: Staking Tip at Bottom (with checkbox to hide) */}
      {showStakingTip && marketplaceDiscount === 0 && isConnected && (
        <div className="mt-4 mx-4 p-3 bg-blue-900/40 border border-blue-500/30 rounded-lg">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 text-blue-200 text-sm">
              💡 <strong>Tip:</strong> Stake 500+ OGB to unlock 50% marketplace discount on all buff and potion purchases!
            </div>
            <label className="flex items-center gap-1.5 text-[10px] text-blue-300 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={!showStakingTip}
                onChange={(e) => setShowStakingTip(!e.target.checked)}
                className="w-3 h-3 rounded border-blue-600 bg-blue-800/50 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
              />
              Don't show again
            </label>
          </div>
        </div>
      )}
    </div>
  )
}

interface BuffCardProps {
  buff: typeof BUFF_METADATA[number]
  pricing?: BuffPricing
  isLoadingPrice: boolean
  onPurchase: () => void
  discount?: number // AGC: NEW (2025-11-24) - Marketplace discount (0 or 0.5)
}

const BuffCard: React.FC<BuffCardProps> = ({ buff, pricing, isLoadingPrice, onPurchase, discount = 0 }) => {
  const Icon = buff.icon

  const priceContent = useMemo(() => {
    if (isLoadingPrice) {
      return <span className="text-xs text-white/70">Loading price...</span>
    }

    if (!pricing) {
      return <span className="text-xs text-white/70">Price unavailable</span>
    }

    // AGC: Apply discount if applicable
    const hasDiscount = discount > 0
    const discountMultiplier = 1 - discount

    return (
      <div className="space-y-1 text-xs text-white/80">
        {hasDiscount && (
          <div className="bg-green-600 text-white px-2 py-1 rounded text-xs font-bold mb-2 text-center">
            🎉 50% OFF (Staked)
          </div>
        )}
        <div className="flex justify-between">
          <span>USDC</span>
          <span>
            {hasDiscount && <span className="line-through text-gray-500 mr-1">{pricing.usdcValue.toFixed(4)}</span>}
            {(pricing.usdcValue * discountMultiplier).toFixed(4)}
          </span>
        </div>
        <div className="flex justify-between">
          <span>ETH</span>
          <span>
            {hasDiscount && <span className="line-through text-gray-500 mr-1">{pricing.ethValue.toFixed(6)}</span>}
            {(pricing.ethValue * discountMultiplier).toFixed(6)}
          </span>
        </div>
        <div className="flex justify-between">
          <span>OGB</span>
          <span>
            {hasDiscount && <span className="line-through text-gray-500 mr-1">{pricing.ogbValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>}
            {(pricing.ogbValue * discountMultiplier).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </span>
        </div>
      </div>
    )
  }, [pricing, isLoadingPrice, discount])

  return (
    <div className={`bg-gradient-to-br ${buff.color} rounded-lg p-4 sm:p-6 border-2 ${buff.borderColor} transform hover:scale-105 transition-transform flex flex-col h-full`}>
      <div className="text-center mb-3 sm:mb-4 flex-shrink-0">
        <div className="text-4xl sm:text-6xl mb-2">{buff.emoji}</div>
        <h3 className="text-base sm:text-xl font-bold text-white">{buff.name}</h3>
      </div>

      <div className="space-y-2 sm:space-y-3 text-white flex-grow flex flex-col">
        <p
          className="text-xs sm:text-sm opacity-90"
          dangerouslySetInnerHTML={{ __html: buff.description }}
        ></p>

        <div className="bg-black/20 rounded-lg p-2">
          <p className="text-xs font-semibold mb-1">Effect:</p>
          <p className="text-xs sm:text-sm">{buff.effect}</p>
        </div>

        <div className="bg-black/20 rounded-lg p-2">
          <p className="text-xs font-semibold mb-1">Proc Rate:</p>
          <p className="text-xs sm:text-sm">{buff.procRate}</p>
        </div>

        <div className="bg-black/30 rounded-lg p-2 sm:p-3 mt-auto">
          <p className="text-xs font-semibold text-white/80 mb-2">Live Price</p>
          {priceContent}
        </div>

        <button
          onClick={onPurchase}
          className="w-full bg-white hover:bg-gray-100 text-gray-900 font-bold py-2 sm:py-3 px-3 sm:px-4 rounded-lg transition-colors flex items-center justify-center gap-2 text-xs sm:text-base flex-shrink-0"
        >
          <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
          Purchase
        </button>
      </div>
    </div>
  )
}

interface PurchaseModalProps {
  buffId: number
  pricing?: BuffPricing
  ogbBalance: number
  getUSDCBalance: (address: string) => Promise<string>
  onClose: () => void
  onPurchase: (tokenId: number, quantity: number, method: PaymentMethod) => Promise<any>
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({
  buffId,
  pricing,
  ogbBalance,
  getUSDCBalance,
  onClose,
  onPurchase,
}) => {
  const buff = BUFF_METADATA.find((b) => b.id === buffId)
  const { address } = useAccount()
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [transactionHash, setTransactionHash] = useState<string | null>(null)
  const [quantity, setQuantity] = useState(1)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('usdc')
  const [usdcBalance, setUsdcBalance] = useState<number | null>(null)
  const [isBalanceLoading, setIsBalanceLoading] = useState(false)

  // AGC: CRITICAL FIX - Don't manipulate document.body in Farcaster (breaks SDK rendering)
  useEffect(() => {
    // AGC: Only set overflow in desktop context - Farcaster handles this internally
    if (typeof window !== 'undefined' && !window.location.pathname.includes('/farcaster/')) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = 'unset'
      }
    }
  }, [])

  useEffect(() => {
    let mounted = true

    const loadUsdcBalance = async () => {
      if (!address) {
        setUsdcBalance(null)
        return
      }

      try {
        setIsBalanceLoading(true)
        const balanceString = await getUSDCBalance(address)
        const numericBalance = Number(balanceString)
        if (mounted) {
          setUsdcBalance(Number.isFinite(numericBalance) ? numericBalance : null)
        }
      } catch (balanceError) {
        console.error('Failed to read USDC balance:', balanceError)
        if (mounted) {
          setUsdcBalance(null)
        }
      } finally {
        if (mounted) {
          setIsBalanceLoading(false)
        }
      }
    }

    loadUsdcBalance()
    return () => {
      mounted = false
    }
  }, [address, getUSDCBalance])

  if (!buff || typeof window === 'undefined') {
    return null
  }

  const totals = useMemo(() => {
    if (!pricing) {
      return {
        usd: 0,
        usdc: 0,
        ogb: 0,
        eth: 0,
      }
    }

    return {
      usd: pricing.usdValue * quantity,
      usdc: pricing.usdcValue * quantity,
      ogb: pricing.ogbValue * quantity,
      eth: pricing.ethValue * quantity,
    }
  }, [pricing, quantity])

  const paymentLabel: Record<PaymentMethod, string> = {
    usdc: `${totals.usdc.toFixed(4)} USDC`,
    eth: `${totals.eth.toFixed(6)} ETH`,
    ogb: `${totals.ogb.toLocaleString(undefined, { maximumFractionDigits: 2 })} OGB`,
  }

  const requiredUsdc = totals.usdc
  const requiredOgb = totals.ogb

  const hasInsufficientUsdc =
    paymentMethod === 'usdc' &&
    usdcBalance !== null &&
    requiredUsdc > 0 &&
    usdcBalance + 1e-6 < requiredUsdc

  const hasInsufficientOgb =
    paymentMethod === 'ogb' &&
    requiredOgb > 0 &&
    ogbBalance + 1e-6 < requiredOgb

  const handlePurchase = async () => {
    if (!address) {
      setError('Please connect your wallet')
      return
    }

    if (!pricing) {
      setError('Pricing data is not available yet. Please try again.')
      return
    }

    if (hasInsufficientUsdc) {
      setError('Insufficient USDC balance. Use the faucet above to grab test USDC before purchasing.')
      return
    }

    if (hasInsufficientOgb) {
      setError('Insufficient OGB balance to complete this purchase.')
      return
    }

    try {
      setIsPurchasing(true)
      setError(null)
      setTransactionHash(null)

      const receipt = await onPurchase(buff.id, quantity, paymentMethod)
      if (receipt?.hash) {
        setTransactionHash(receipt.hash)
      }

      console.log(`✅ Purchase confirmed! TX: ${receipt?.hash}`)
      setTimeout(async () => {
        await alert(
          `Successfully purchased ${quantity}x ${buff.name}! 🎉\n\nTransaction: ${
            receipt?.hash ?? 'Unknown'
          }`,
          { type: 'success', title: 'Purchase Successful!' }
        )
        onClose()
      }, 800)
    } catch (err: any) {
      console.error('Purchase failed:', err)
      setError(err?.shortMessage || err?.message || 'Purchase failed. Please try again.')
    } finally {
      setIsPurchasing(false)
    }
  }

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity < 1) {
      setQuantity(1)
      return
    }
    if (newQuantity > 25) {
      setQuantity(25)
      return
    }
    setQuantity(newQuantity)
  }

  const isPricingReady = !!pricing && pricing.usdValue > 0

  const modalContent = (
    <div className="fixed inset-0 bg-slate-950 flex items-center justify-center z-50">
      <div className="w-full max-w-sm mx-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
        {/* AGC: Game-style header with icon */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 py-3 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-4xl">{buff.emoji}</div>
              <div>
                <h2 className="text-lg font-bold text-white leading-tight">{buff.name}</h2>
                <p className="text-xs text-slate-400">{buff.effect}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* AGC: Compact content area */}
        <div className="px-4 py-4 space-y-3">
          {/* AGC: Price display - game-style compact */}
          <div className="bg-slate-950/50 rounded-lg p-3 border border-slate-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-400 uppercase tracking-wide">Price</span>
              <span className="text-lg font-bold text-green-400">
                {pricing ? `$${pricing.usdValue.toFixed(4)}` : '—'}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-1.5 text-[10px]">
              <div className="bg-slate-800/60 rounded px-2 py-1.5 text-center">
                <p className="text-slate-400 mb-0.5">USDC</p>
                <p className="text-white font-semibold">
                  {pricing ? pricing.usdcValue.toFixed(4) : '—'}
                </p>
              </div>
              <div className="bg-slate-800/60 rounded px-2 py-1.5 text-center">
                <p className="text-slate-400 mb-0.5">ETH</p>
                <p className="text-white font-semibold">
                  {pricing ? pricing.ethValue.toFixed(6) : '—'}
                </p>
              </div>
              <div className="bg-slate-800/60 rounded px-2 py-1.5 text-center">
                <p className="text-slate-400 mb-0.5">OGB</p>
                <p className="text-white font-semibold">
                  {pricing ? pricing.ogbValue.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}
                </p>
              </div>
            </div>
          </div>

          {/* AGC: Quantity selector - compact */}
          <div className="bg-slate-950/50 rounded-lg p-3 border border-slate-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-400 uppercase tracking-wide">Quantity</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleQuantityChange(quantity - 1)}
                className="w-10 h-10 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xl font-bold transition-colors flex-shrink-0"
              >
                −
              </button>
              <div className="flex-1 text-center bg-slate-900 border border-slate-700 rounded-lg py-2.5">
                <span className="text-white font-bold text-lg">{quantity}</span>
              </div>
              <button
                onClick={() => handleQuantityChange(quantity + 1)}
                className="w-10 h-10 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xl font-bold transition-colors flex-shrink-0"
              >
                +
              </button>
            </div>
          </div>

          {/* AGC: Payment method - game-style buttons */}
          <div className="bg-slate-950/50 rounded-lg p-3 border border-slate-700/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-400 uppercase tracking-wide">Payment</span>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {(['usdc', 'eth', 'ogb'] as PaymentMethod[]).map((method) => (
                <button
                  key={method}
                  onClick={() => setPaymentMethod(method)}
                  className={`rounded-lg px-2 py-2 text-xs font-bold transition-all ${
                    paymentMethod === method
                      ? 'bg-gradient-to-br from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/40'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <div className="font-semibold">{method.toUpperCase()}</div>
                  <div className="text-[10px] mt-0.5 opacity-90">{paymentLabel[method]}</div>
                </button>
              ))}
            </div>
            {paymentMethod === 'usdc' && (
              <p className="text-[10px] text-slate-400 mt-2 text-center">
                Balance: {isBalanceLoading ? '...' : usdcBalance !== null ? `${usdcBalance.toFixed(4)} USDC` : 'N/A'}
              </p>
            )}
            {hasInsufficientUsdc && (
              <p className="text-[10px] text-yellow-300 mt-1.5 text-center">⚠️ Insufficient USDC</p>
            )}
            {paymentMethod === 'ogb' && hasInsufficientOgb && (
              <p className="text-[10px] text-yellow-300 mt-1.5 text-center">⚠️ Need {requiredOgb.toFixed(2)} OGB</p>
            )}
          </div>

          {/* AGC: Error/Status messages - compact */}
          {error && (
            <div className="bg-red-900/60 border border-red-500/50 rounded-lg p-2.5 text-xs text-red-200 text-center">
              ⚠️ {error}
            </div>
          )}

          {transactionHash && (
            <div className="bg-green-900/40 border border-green-500/50 rounded-lg p-2.5 text-[10px] text-green-200 text-center break-all">
              ✓ TX: {transactionHash.slice(0, 20)}...
            </div>
          )}

          {/* AGC: Action buttons - game-style */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={onClose}
              className="flex-1 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handlePurchase}
              disabled={isPurchasing || !isPricingReady}
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-slate-600 disabled:to-slate-600 disabled:text-slate-200 text-white font-bold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-sm"
            >
              <DollarSign className="w-4 h-4" />
              {isPurchasing ? 'Processing...' : `Pay ${paymentLabel[paymentMethod]}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
